import React, { useState, useRef, useEffect } from 'react';
import Box from '@mui/joy/Box';
import Sheet from '@mui/joy/Sheet';
import IconButton from '@mui/joy/IconButton';
import Typography from '@mui/joy/Typography';
import Textarea from '@mui/joy/Textarea';
import Button from '@mui/joy/Button';
import CircularProgress from '@mui/joy/CircularProgress';
import Stack from '@mui/joy/Stack';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import { marked } from 'marked';

import { assistantService } from '../../api/services';

marked.use({ gfm: true, breaks: true });

// Compact markdown styling so headings/lists/code/tables read well in a bubble.
const mdSx = {
  fontSize: '14px', lineHeight: 1.5, wordBreak: 'break-word',
  '& > :first-of-type': { mt: 0 }, '& > :last-child': { mb: 0 },
  '& h1, & h2, & h3, & h4': { fontSize: 'md', fontWeight: 'lg', mt: 1, mb: 0.5 },
  '& p': { my: 0.5 },
  '& ul, & ol': { pl: 2.5, my: 0.5 },
  '& li': { my: 0.25 },
  '& code': {
    bgcolor: 'background.level2', px: 0.5, borderRadius: '4px',
    fontFamily: 'monospace', fontSize: '0.85em',
  },
  '& pre': {
    bgcolor: 'background.level2', p: 1, borderRadius: 'sm',
    overflowX: 'auto', my: 0.5,
  },
  '& pre code': { bgcolor: 'transparent', px: 0 },
  '& table': { borderCollapse: 'collapse', width: '100%', my: 0.5 },
  '& th, & td': {
    border: '1px solid', borderColor: 'divider',
    px: 0.75, py: 0.25, textAlign: 'left', fontSize: '0.85em',
  },
  '& a': { color: 'primary.500' },
  '& blockquote': {
    borderLeft: '3px solid', borderColor: 'divider',
    pl: 1, my: 0.5, color: 'text.tertiary',
  },
};

const WELCOME = {
  role: 'assistant',
  content:
    "Hi! I'm the Vocative assistant. Ask me how anything works in the creator " +
    "dashboard, or what your end users see after you share a project with them.",
};

export default function AssistantChat({ context = null }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;

    const nextMessages = [...messages, { role: 'user', content: text }];
    setMessages(nextMessages);
    setInput('');
    setLoading(true);

    // History excludes the synthetic welcome message and the just-added turn.
    const history = messages
      .filter((m) => m !== WELCOME)
      .map((m) => ({ role: m.role, content: m.content }));

    try {
      const data = await assistantService.sendMessage(text, history, context);
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: data.response || '(no response)' },
      ]);
    } catch (e) {
      const msg =
        e?.response?.data?.error ||
        'Sorry, something went wrong. Please try again.';
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: msg },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  if (!open) {
    return (
      <IconButton
        variant="solid"
        color="primary"
        size="lg"
        onClick={() => setOpen(true)}
        sx={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 1300,
          borderRadius: '50%', width: 56, height: 56, boxShadow: 'lg',
        }}
        aria-label="Open assistant"
      >
        <ChatBubbleOutlineIcon />
      </IconButton>
    );
  }

  return (
    <Sheet
      variant="outlined"
      sx={{
        position: 'fixed', bottom: 24, right: 24, zIndex: 1300,
        width: { xs: 'calc(100vw - 32px)', sm: 400 },
        height: { xs: 'calc(100vh - 120px)', sm: 560 },
        maxHeight: 'calc(100vh - 48px)',
        display: 'flex', flexDirection: 'column',
        borderRadius: 'lg', boxShadow: 'lg', overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider',
      }}>
        <Typography level="title-md">Vocative Assistant</Typography>
        <IconButton size="sm" variant="plain" onClick={() => setOpen(false)} aria-label="Close assistant">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Messages */}
      <Box ref={scrollRef} sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
        <Stack spacing={1.5}>
          {messages.map((m, i) => (
            <Box
              key={i}
              sx={{
                alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                maxWidth: '85%',
                px: 1.5, py: 1, borderRadius: 'md',
                bgcolor: m.role === 'user' ? 'primary.softBg' : 'neutral.softBg',
                wordBreak: 'break-word',
              }}
            >
              {m.role === 'assistant' ? (
                <Box sx={mdSx} dangerouslySetInnerHTML={{ __html: marked.parse(m.content || '') }} />
              ) : (
                <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap' }}>{m.content}</Typography>
              )}
            </Box>
          ))}
          {loading && (
            <Box sx={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1 }}>
              <CircularProgress size="sm" />
              <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>Thinking…</Typography>
            </Box>
          )}
        </Stack>
      </Box>

      {/* Input */}
      <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider', display: 'flex', gap: 1, alignItems: 'flex-end' }}>
        <Textarea
          placeholder="Ask about the creator or the end-user experience…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          minRows={1}
          maxRows={4}
          sx={{ flex: 1 }}
          disabled={loading}
        />
        <Button
          onClick={send}
          disabled={loading || !input.trim()}
          sx={{ minWidth: 0, px: 1.5 }}
          aria-label="Send"
        >
          <SendIcon fontSize="small" />
        </Button>
      </Box>
    </Sheet>
  );
}
